import * as React from 'react';
import styled from 'styled-components';
import { pan, zoom } from './zooming';
import { clamp, throttle } from 'lodash';
import { getTarget } from './getTarget';
import { ChartWindowState, NamedNumericRange, PanZoomClient } from './types';
import { Point } from '../../Point';
import { Excluded, MouseButton, isMac } from '@ardoq/common-helpers';

export const MOUSE_MOVE_EVENT_THROTTLE = 50;
const ZOOM_THROTTLE = 50;
const PINCH_DELTA_MULTIPLIER = 4;
const LINE_HEIGHT_PX = 16;

const normalizeWheelDelta = (e: WheelEvent): number => {
  if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return e.deltaY * LINE_HEIGHT_PX;
  }

  if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return e.deltaY * window.innerHeight;
  }

  return e.deltaY;
};

const Container = styled.div`
  height: 100%;
`;

/**
 * Properties that panAndZoom will send to the wrapped component
 */
type PanZoomInjectedProps = {
  isPanning?: () => boolean;
  registerPanZoomClient?: (client: PanZoomClient) => void;
};

/**
 * Additional properties that the panAndZoom HOC will expect in addition to the
 * props of the wrapped component
 */
type PanZoomOwnProps = ChartWindowState & {
  linkHandler?: { isLinking: boolean };
  setWindow: ({ windowCenter, windowScale }: ChartWindowState) => void;
  onPanEnd?: () => void;
  scaleLimits?: NamedNumericRange;
};

export function panAndZoom<WrappedProps extends PanZoomInjectedProps>(
  WrappedComponent: React.ComponentType<WrappedProps & PanZoomInjectedProps>
): React.ComponentType<
  Excluded<WrappedProps, PanZoomInjectedProps> & PanZoomOwnProps
> {
  return class extends React.Component<
    Excluded<WrappedProps, PanZoomInjectedProps> & PanZoomOwnProps
  > {
    private zoomClient: PanZoomClient | null = null;
    private panningFrom: Point | null = null;
    private panningTo: Point | null = null;
    private panningCenter = this.props.windowCenter;
    private newWindow: ChartWindowState | null = null;

    private throttledHandlePan = throttle(
      (e: MouseEvent) => {
        if (!this.panningFrom) {
          return;
        }
        this.panningTo = [e.clientX, e.clientY];
        this.onWindowChanged();
        const dimensions = this.getDimensions();
        const newCenter = pan(
          [dimensions.chart.width, dimensions.chart.height],
          this.panningCenter,
          this.props.windowScale,
          this.panningFrom,
          this.panningTo
        );

        const newWindow = {
          windowCenter: newCenter,
          windowScale: this.props.windowScale,
        };

        this.props.setWindow(newWindow);
      },
      MOUSE_MOVE_EVENT_THROTTLE,
      {
        leading: false,
      }
    );
    private throttledDispatchZoom = throttle(() => {
      if (!this.newWindow) {
        return;
      }
      const [newX, newY] = this.newWindow.windowCenter;
      const [propX, propY] = this.props.windowCenter;
      if (
        this.newWindow.windowScale !== this.props.windowScale ||
        newX !== propX ||
        newY !== propY
      ) {
        this.onWindowChanged();
        this.props.setWindow(this.newWindow);
      }
      this.newWindow = null;
    }, ZOOM_THROTTLE);
    componentDidMount() {
      if (!this.zoomClient) {
        return;
      }

      this.zoomClient
        .getMouseWheelElement()
        .addEventListener('wheel', this.handleZoom);
    }

    componentWillUnmount() {
      this.throttledDispatchZoom.cancel();
      this.throttledHandlePan.cancel();

      if (!this.zoomClient) {
        return;
      }
      this.zoomClient
        .getMouseWheelElement()
        .removeEventListener('wheel', this.handleZoom);

      document.removeEventListener('dblclick', this.handleComponentDoubleClick);

      this.zoomClient = null;
    }

    render() {
      return (
        <Container onMouseDown={this.startPanning}>
          <WrappedComponent
            {...(this.props as any)}
            isPanning={this.isPanning}
            registerPanZoomClient={this.registerPanZoomClient}
          />
        </Container>
      );
    }
    private isPanning = () => !!this.panningTo;
    private getDimensions = () => this.zoomClient!.getDimensions();
    private offsetToAxes = (e: WheelEvent) => this.zoomClient!.offsetToAxes(e);
    private onWindowChanged = () => this.zoomClient?.onWindowChanged();
    private startPanning = (e: React.MouseEvent) => {
      if (e.button !== MouseButton.PRIMARY) {
        return;
      }
      e.preventDefault();
      document.addEventListener('mouseup', this.mouseUp);
      document.addEventListener('dblclick', this.handleComponentDoubleClick);
      if (this.props.windowScale === 1.0) {
        // can't pan at full scale.
        return;
      }

      document.addEventListener('mousemove', this.throttledHandlePan);
      this.panningFrom = [e.clientX, e.clientY];
      this.panningCenter = this.props.windowCenter;
    };
    private handleZoom = (e: WheelEvent) => {
      e.preventDefault();
      const windowCenter =
        (this.newWindow && this.newWindow!.windowCenter) ||
        this.props.windowCenter;
      const windowScale =
        (this.newWindow && this.newWindow!.windowScale) ||
        this.props.windowScale;

      const dimensions = this.getDimensions();
      const newWindow = zoom(
        {
          width: dimensions.chart.width,
          height: dimensions.chart.height,
        },
        windowCenter,
        this.offsetToAxes(e),
        windowScale,
        (e.ctrlKey ? PINCH_DELTA_MULTIPLIER : 1) * normalizeWheelDelta(e)
      );
      const { scaleLimits } = this.props;
      if (scaleLimits) {
        const clampedScale = clamp(
          newWindow.windowScale,
          scaleLimits.min,
          scaleLimits.max
        );
        this.newWindow = {
          windowScale: clampedScale,
          windowCenter: this.resolveWindowCenter(
            clampedScale,
            newWindow,
            scaleLimits
          ),
        };
      } else {
        this.newWindow = newWindow;
      }
      this.throttledDispatchZoom();
    };
    private handleSelectComponent = (e: MouseEvent) => {
      const target = getTarget(e.target) as HTMLElement | null;

      if (!target || !target.dataset) {
        // no closest component found. probably this is a click directly on the <svg> element.
        // or maybe it's Edge <= v16 and SVGElement has no dataset property.
        return;
      }
      if (!target.dataset.globalHandlerId || !this.zoomClient) return;
      this.zoomClient.selectComponent?.(
        {
          cid: target.dataset.globalHandlerId,
        },
        e
      );
    };
    private handleComponentDoubleClick = (e: MouseEvent) => {
      const target = getTarget(e.target) as HTMLElement | null;

      if (!target || !target.dataset) {
        // no closest component found. probably this is a click directly on the <svg> element.
        // or maybe it's Edge <= v16 and SVGElement has no dataset property.
        return;
      }
      if (
        !target.dataset.globalHandlerId ||
        !this.zoomClient ||
        !this.zoomClient?.onComponentDoubleClick
      )
        return;
      this.zoomClient.onComponentDoubleClick(
        {
          cid: target.dataset.globalHandlerId,
        },
        e
      );
    };
    private mouseUp = (e: MouseEvent) => {
      if (
        e.button !== MouseButton.PRIMARY ||
        (isMac() && e.ctrlKey) ||
        this.props.linkHandler?.isLinking
      ) {
        return;
      }
      e.preventDefault();
      document.removeEventListener('mouseup', this.mouseUp);
      document.removeEventListener('mousemove', this.throttledHandlePan);
      this.panningFrom = null;
      if (this.panningTo) {
        // stop panning
        this.panningTo = null;
        this.props.onPanEnd?.();
        this.setState({}); // redraw
        return;
      }
      if (this.zoomClient?.onComponentDoubleClick) {
        // in ardoq-front visualizations only utilize selectComponent on double click, via the selectComponent prop
        // in discover visualizations use selectComponent on single click and recontextualisation on double click
        // because of this, we need to make sure the single click handler is only active when both onComponentDoubleClick and selectComponent functions exist
        this.handleSelectComponent(e);
      }
    };
    private resolveWindowCenter = (
      clampedScale: number,
      newWindow: ChartWindowState,
      scaleLimits: NamedNumericRange
    ): Point => {
      const fullyZoomedOutCenter: Point = [0.5, 0.5];
      const scaleWasClamped = clampedScale !== newWindow.windowScale;

      if (clampedScale >= scaleLimits.max) {
        return fullyZoomedOutCenter;
      }
      if (scaleWasClamped) {
        return this.props.windowCenter;
      }
      return newWindow.windowCenter;
    };
    private registerPanZoomClient = (client: PanZoomClient) =>
      (this.zoomClient = client);
  };
}
